import { apiFetch, apiJson } from './client';
import { ApiProblem } from './problem';
import type { Session, DeliberationState } from './deliberation';

/** Les documents produits depuis une délibération close (D61). */
export type DocumentType = 'transcript' | 'minutes' | 'certificate' | 'enrolment' | 'card' | 'list' | 'internship';

/** Les types aux signataires réglés (D61, D78) ; les autres prennent ceux de leur modèle. */
export type SignatoryType = 'transcript' | 'minutes' | 'certificate' | 'enrolment';

export interface DocumentDeliberation {
    readonly id: string;
    readonly training: {
        readonly id: string;
        readonly code: string;
        readonly name: string;
    };
    readonly session: Session;
    readonly state: DeliberationState;
    readonly closedAt: string | null;
    readonly run: { readonly id: string; readonly number: number } | null;
    readonly students: number;
    readonly admitted: number;
    readonly issue: boolean;
}

export interface DocumentBatchSummary {
    readonly id: string;
    readonly type: DocumentType;
    readonly deliberationId: string;
    readonly training: { readonly code: string; readonly name: string };
    readonly session: Session;
    readonly run: number;
    readonly count: number;
    readonly published: number;
    readonly superseded: number;
    readonly issuedAt: string;
    readonly issuedBy: string;
    readonly reference: string;
    readonly documentId: string | null;
}

export interface DocumentOverview {
    readonly years: readonly { readonly id: string; readonly label: string }[];
    readonly year: { readonly id: string; readonly label: string } | null;
    readonly deliberations: readonly DocumentDeliberation[];
    readonly batches: readonly DocumentBatchSummary[];
    readonly can: { readonly issue: boolean; readonly settings: boolean };
}

export interface IssuedDocument {
    readonly id: string;
    readonly reference: string;
    readonly student: {
        readonly id: string;
        readonly number: string | null;
        readonly name: string;
    } | null;
    readonly publishedAt: string | null;
    readonly supersededAt: string | null;
}

export interface DocumentBatch {
    readonly id: string;
    readonly batch: {
        readonly type: DocumentType;
        readonly training: { readonly code: string; readonly name: string };
        readonly session: Session;
        readonly year: string;
        readonly issuedAt: string;
        readonly issuedBy: string;
        readonly issue: boolean;
    };
    readonly documents: readonly IssuedDocument[];
}

export interface Verification {
    readonly reference: string;
    readonly state: 'valid' | 'unsigned' | 'superseded' | 'altered' | 'cancelled' | 'expired' | 'unknown';
    readonly type?: DocumentType;
    readonly institution?: string;
    readonly student?: string | null;
    readonly training?: string;
    readonly year?: string;
    readonly session?: Session | null;
    readonly run?: number | null;
    readonly issuedAt?: string;
    readonly issuedBy?: string;
    readonly supersededAt?: string | null;
    readonly cancelledAt?: string | null;
    readonly validUntil?: string | null;
    readonly signedAt?: string | null;
    readonly signedBy?: string | null;
}

export interface DocumentCommandResult {
    readonly id: string | null;
    readonly count: number | null;
    readonly documentId: string | null;
}

export type DocumentOptions = Readonly<Record<string, string | boolean>>;

export function getDocumentOverview(year: string | null): Promise<DocumentOverview> {
    return apiJson<DocumentOverview>(`/api/documents/overview${year === null ? '' : `?year=${encodeURIComponent(year)}`}`);
}

export function getDocumentBatch(id: string): Promise<DocumentBatch> {
    return apiJson<DocumentBatch>(`/api/documents/batches/${encodeURIComponent(id)}`);
}

export function runDocumentCommand(action: string, target: string | null, values: Readonly<Record<string, unknown>> = {}): Promise<DocumentCommandResult> {
    return apiJson<DocumentCommandResult>('/api/document/commands', {
        method: 'POST',
        body: JSON.stringify({ action, target, values }),
    });
}

function query(options: DocumentOptions): string {
    return Object.entries(options)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(typeof value === 'boolean' ? (value ? '1' : '0') : value)}`)
        .join('&');
}

/** L'aperçu d'un document avant production (planches 105 à 107), en HTML. */
export async function getDocumentPreview(type: DocumentType, deliberation: string, student: string | null, options: DocumentOptions): Promise<string> {
    const path = `/api/documents/preview?type=${type}&deliberation=${encodeURIComponent(deliberation)}${student === null ? '' : `&student=${encodeURIComponent(student)}`}&${query(options)}`;
    const response = await apiFetch(path, { headers: { Accept: 'text/html' } });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return response.text();
}

export function documentPdfPath(id: string): string {
    return `/api/documents/${encodeURIComponent(id)}/pdf`;
}

export function documentXlsxPath(id: string): string {
    return `/api/documents/${encodeURIComponent(id)}/xlsx`;
}

export function batchArchivePath(batchId: string): string {
    return `/api/documents/batches/${encodeURIComponent(batchId)}/archive`;
}

/** Télécharge un fichier servi derrière le jeton (Excel, archive zip). */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
    const response = await apiFetch(path, { headers: { Accept: '*/*' } });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    const disposition = response.headers.get('Content-Disposition') ?? '';
    const name = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = decodeURIComponent(name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** La vérification publique d'une référence (sans session). */
export async function verifyDocument(reference: string): Promise<Verification> {
    const response = await apiFetch(`/api/public/documents/${encodeURIComponent(reference.trim())}`);

    if (response.status === 404) {
        return { reference: reference.trim().toUpperCase(), state: 'unknown' };
    }

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as Verification;
}

export interface Signatory {
    readonly title: string;
    readonly name: string | null;
}

export interface DocumentSettingsView {
    readonly place: string | null;
    readonly tagline: string | null;
    readonly signatories: Readonly<Record<SignatoryType, readonly Signatory[]>>;
    readonly editable: boolean;
    readonly enabled: boolean;
    readonly publishDefault: boolean;
    readonly validityMonths: number | null;
    readonly shortName: boolean;
    readonly duplicateMark: boolean;
    readonly configure: boolean;
}

/** D78 : les réglages de diffusion et de vérification, à côté de l'en-tête et des signataires. */
export interface DocumentPolicy {
    readonly publishDefault: boolean;
    readonly validityMonths: number | null;
    readonly shortName: boolean;
    readonly duplicateMark: boolean;
}

export function getDocumentSettings(): Promise<DocumentSettingsView> {
    return apiJson<DocumentSettingsView>('/api/documents/settings');
}

export function saveDocumentSettings(place: string, tagline: string, signatories: Readonly<Record<SignatoryType, readonly Signatory[]>>, policy?: DocumentPolicy): Promise<DocumentCommandResult> {
    return runDocumentCommand('document.settings', null, {
        place,
        tagline,
        signatories,
        ...(policy ?? {}),
    });
}

// ---------------------------------------------------------------- D78 : la gestion des documents (DG-1)

/** L'état d'un document produit, tel que le registre et la vérification le disent. */
export type DocumentState = 'valid' | 'unsigned' | 'superseded' | 'cancelled' | 'expired';

export const DOCUMENT_STATES: readonly DocumentState[] = ['valid', 'unsigned', 'superseded', 'cancelled', 'expired'];

interface Year {
    readonly id: string;
    readonly label: string;
}

export interface DocumentDashboard {
    readonly years: readonly Year[];
    readonly year: Year | null;
    readonly totals: {
        readonly produced: number;
        readonly published: number;
        readonly todo: number;
        readonly verifications: number;
    };
    readonly types: readonly {
        readonly type: DocumentType;
        readonly produced: number;
        readonly published: number;
        readonly cancelled: number;
        readonly downloads: number;
        readonly verifications: number;
    }[];
    readonly todo: readonly {
        readonly kind: 'transcripts' | 'enrolment';
        readonly deliberationId: string | null;
        readonly training: {
            readonly code: string;
            readonly name: string;
        } | null;
        readonly session: Session | null;
        readonly closedAt: string | null;
        readonly count: number;
    }[];
    readonly batches: readonly {
        readonly id: string;
        readonly type: DocumentType;
        readonly training: { readonly code: string; readonly name: string };
        readonly session: Session | null;
        readonly count: number;
        readonly published: number;
        readonly issuedAt: string;
        readonly issuedBy: string;
    }[];
    readonly verifications: readonly {
        readonly at: string;
        readonly result: Exclude<Verification['state'], 'unknown'> | null;
        readonly id: string;
        readonly reference: string;
        readonly type: DocumentType;
    }[];
    readonly can: { readonly issue: boolean; readonly configure: boolean };
}

export interface RegistryFilters {
    readonly q: string;
    readonly type: DocumentType | '';
    readonly training: string;
    readonly state: DocumentState | '';
    readonly batch?: string;
}

export interface RegistryItem {
    readonly id: string;
    readonly reference: string;
    readonly type: DocumentType;
    readonly training: {
        readonly id: string;
        readonly code: string;
        readonly name: string;
    };
    readonly session: Session | null;
    readonly student: {
        readonly name: string;
        readonly number: string | null;
    } | null;
    readonly issuedAt: string;
    readonly issuedBy: string;
    readonly published: boolean;
    readonly state: DocumentState;
    readonly downloads: number;
    readonly verifications: number;
}

export interface DocumentRegistry {
    readonly years: readonly Year[];
    readonly year: Year | null;
    readonly trainings: readonly {
        readonly id: string;
        readonly code: string;
        readonly name: string;
    }[];
    readonly filters: RegistryFilters;
    readonly items: readonly RegistryItem[];
    readonly total: number;
    readonly page: number;
    readonly pageSize: number;
}

export type DocumentEventKind = 'downloaded' | 'verified' | 'duplicate' | 'published' | 'unpublished' | 'cancelled' | 'replaced' | 'signed' | 'returned';

export interface DocumentItem {
    readonly id: string;
    readonly reference: string;
    readonly type: DocumentType;
    readonly state: DocumentState;
    readonly training: {
        readonly id: string;
        readonly code: string;
        readonly name: string;
    };
    readonly year: string;
    readonly session: Session | null;
    readonly run: number | null;
    readonly student: {
        readonly id: string;
        readonly name: string;
        readonly number: string | null;
    } | null;
    readonly lang: string;
    readonly validUntil: string | null;
    readonly signatories: readonly string[];
    readonly digest: string;
    readonly issuedAt: string;
    readonly issuedBy: string;
    readonly batchId: string;
    readonly publishedAt: string | null;
    readonly supersededAt: string | null;
    readonly cancelledAt: string | null;
    readonly cancelReason: string | null;
    readonly cancelledBy: string | null;
    readonly replacedBy: {
        readonly id: string;
        readonly reference: string;
    } | null;
    readonly replaces: {
        readonly id: string;
        readonly reference: string;
    } | null;
    readonly verifyUrl: string;
    readonly events: readonly {
        readonly kind: DocumentEventKind;
        readonly at: string;
        readonly actor: string | null;
        readonly result: Exclude<Verification['state'], 'unknown'> | null;
        readonly detail: string | null;
    }[];
    readonly counts: {
        readonly downloads: number;
        readonly verifications: number;
        readonly duplicates: number;
    };
    readonly can: {
        readonly issue: boolean;
        readonly cancel: boolean;
        readonly replace: boolean;
        readonly xlsx: boolean;
    };
}

async function page<T>(path: string): Promise<T> {
    return (await apiJson<{ readonly data: T }>(path)).data;
}

export function getDocumentDashboard(year: string | null): Promise<DocumentDashboard> {
    return page<DocumentDashboard>(`/api/documents/dashboard${year === null ? '' : `?year=${encodeURIComponent(year)}`}`);
}

export function getDocumentRegistry(year: string | null, filters: RegistryFilters, pageNumber: number): Promise<DocumentRegistry> {
    const params = new URLSearchParams();

    if (year !== null) {
        params.set('year', year);
    }

    for (const [key, value] of Object.entries(filters)) {
        if (value !== '') {
            params.set(key, value);
        }
    }

    params.set('page', String(pageNumber));

    return page<DocumentRegistry>(`/api/documents/registry?${params.toString()}`);
}

export function getDocumentItem(id: string): Promise<DocumentItem> {
    return page<DocumentItem>(`/api/documents/items/${encodeURIComponent(id)}`);
}

export function duplicatePdfPath(id: string): string {
    return `${documentPdfPath(id)}?duplicate=1`;
}

export function cancelDocument(id: string, reason: string): Promise<DocumentCommandResult> {
    return runDocumentCommand('document.cancel', id, { reason });
}

export function replaceDocument(id: string): Promise<DocumentCommandResult> {
    return runDocumentCommand('document.replace', id);
}

export function switchDocumentModule(enabled: boolean): Promise<DocumentCommandResult> {
    return runDocumentCommand('document.module', null, { enabled });
}

// ---------------------------------------------------------------- D79 : les modèles de documents (DG-2)

export type TemplateKind = 'transcript' | 'certificate' | 'minutes' | 'enrolment' | 'card' | 'list' | 'internship';
export type TemplateSource = 'deliberation' | 'enrolment' | 'class' | 'internship';
export type TemplateCategory = 'schooling' | 'results' | 'lists' | 'finance' | 'stages' | 'teachers';
export type TemplatePaper = 'A4' | 'A4-landscape' | 'A5' | 'CR80';
export type TemplateLang = 'fr' | 'en' | 'ar';
export type Localized = Partial<Record<TemplateLang, string>>;

export const TEMPLATE_KINDS: readonly TemplateKind[] = ['transcript', 'certificate', 'minutes', 'enrolment', 'card', 'list', 'internship'];
export const TEMPLATE_CATEGORIES: readonly TemplateCategory[] = ['schooling', 'results', 'lists', 'finance', 'stages', 'teachers'];
export const TEMPLATE_PAPERS: readonly TemplatePaper[] = ['A4', 'A4-landscape', 'A5', 'CR80'];
export const TEMPLATE_LANGS: readonly TemplateLang[] = ['fr', 'en', 'ar'];

/** Un bloc d'une page (planche 259) ; ses réglages dépendent de son type. */
export interface TemplateBlock {
    readonly id: string;
    readonly type: string;
    readonly if?: string;
    readonly [key: string]: unknown;
}

export interface TemplateSettings {
    readonly validityMonths: number | null | 'inherit';
    readonly duplicateMark: boolean;
    readonly signatureRequired: boolean;
    readonly font: 'sans' | 'serif';
    readonly fontSize: 'small' | 'normal' | 'large';
    readonly margins: 'narrow' | 'normal' | 'wide';
}

export interface TemplateMeta {
    readonly id: string;
    readonly code: string;
    readonly name: string;
    readonly category: TemplateCategory;
    readonly kind: TemplateKind;
    readonly source: TemplateSource;
    readonly paper: TemplatePaper;
    readonly languages: readonly TemplateLang[];
    readonly verifiable: boolean;
    readonly system: boolean;
    readonly default: boolean;
    readonly archived: boolean;
    readonly legacyCode: string | null;
}

export interface TemplateSummary extends TemplateMeta {
    readonly current: number | null;
    readonly draft: number | null;
    readonly trainings: number;
    readonly documents: number;
}

export interface TemplateCatalogue {
    readonly templates: readonly TemplateSummary[];
}

export interface TemplateVersion {
    readonly id: string;
    readonly number: number;
    readonly blocks: readonly TemplateBlock[];
    readonly settings: TemplateSettings;
    readonly createdAt: string;
    readonly createdBy: string;
    readonly publishedAt: string | null;
    readonly publishedBy: string | null;
    readonly note: string | null;
}

export interface TemplateDetail extends TemplateMeta {
    readonly current: TemplateVersion | null;
    readonly draft: TemplateVersion | null;
    readonly versions: readonly TemplateVersion[];
    readonly fields: Readonly<Record<string, readonly string[]>>;
    readonly blocks: readonly string[];
    readonly conditions: readonly string[];
    readonly subjects: readonly TemplateSubject[];
    readonly assignments: readonly {
        readonly id: string;
        readonly training: {
            readonly id: string;
            readonly code: string;
            readonly name: string;
            readonly year: string;
        };
        readonly default: boolean;
    }[];
    readonly can: { readonly edit: boolean };
}

export interface TemplateSubject {
    readonly id: string;
    readonly label: string;
}

export interface AssignmentTitles {
    readonly normal: string | null;
    readonly retake: string | null;
    readonly credit: string | null;
    readonly shared: string | null;
}

export interface AssignmentCell {
    readonly id: string;
    readonly template: { readonly id: string; readonly name: string };
    readonly default: boolean;
    readonly titles: AssignmentTitles;
}

export interface TemplateAssignments {
    readonly years: readonly Year[];
    readonly year: Year | null;
    readonly previous: Year | null;
    readonly kinds: readonly TemplateKind[];
    readonly trainings: readonly {
        readonly id: string;
        readonly code: string;
        readonly name: string;
        readonly cells: Partial<Record<TemplateKind, readonly AssignmentCell[]>>;
    }[];
    readonly templates: Partial<
        Record<
            TemplateKind,
            readonly {
                readonly id: string;
                readonly name: string;
                readonly default: boolean;
                readonly system: boolean;
            }[]
        >
    >;
}

export function getTemplateCatalogue(): Promise<TemplateCatalogue> {
    return page<TemplateCatalogue>('/api/documents/templates');
}

export function getTemplate(id: string): Promise<TemplateDetail> {
    return page<TemplateDetail>(`/api/documents/templates/${encodeURIComponent(id)}`);
}

export async function getTemplateSubjects(id: string, q: string): Promise<readonly TemplateSubject[]> {
    return (await page<{ readonly subjects: readonly TemplateSubject[] }>(`/api/documents/templates/${encodeURIComponent(id)}/subjects?q=${encodeURIComponent(q)}`)).subjects;
}

export function getTemplateAssignments(year: string | null): Promise<TemplateAssignments> {
    return page<TemplateAssignments>(`/api/documents/template-assignments${year === null ? '' : `?year=${encodeURIComponent(year)}`}`);
}

interface PreviewRequest {
    readonly blocks: readonly TemplateBlock[];
    readonly settings: TemplateSettings;
    readonly lang: TemplateLang;
    readonly subject: string | null;
}

/** L'aperçu d'un brouillon, même non enregistré, en HTML. */
export async function previewTemplate(id: string, request: PreviewRequest, signal?: AbortSignal): Promise<string> {
    const response = await apiFetch(`/api/documents/templates/${encodeURIComponent(id)}/preview`, {
        method: 'POST',
        headers: {
            Accept: 'text/html',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        ...(signal === undefined ? {} : { signal }),
    });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return response.text();
}

/** L'aperçu en PDF, ouvert dans un nouvel onglet. */
export async function previewTemplatePdf(id: string, request: PreviewRequest): Promise<void> {
    const opened = window.open('', '_blank');
    const response = await apiFetch(`/api/documents/templates/${encodeURIComponent(id)}/preview?format=pdf`, {
        method: 'POST',
        headers: {
            Accept: 'application/pdf',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        opened?.close();
        throw await ApiProblem.from(response);
    }

    const url = URL.createObjectURL(await response.blob());

    if (opened === null) {
        window.location.assign(url);
    } else {
        opened.location.href = url;
    }

    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* D80 : la production par lot et la signature. */

export type JobState = 'queued' | 'running' | 'done' | 'partial' | 'stopped' | 'failed' | 'to_sign' | 'signed' | 'returned';
export type SubjectWarning = 'no_photo' | 'no_number' | 'existing';

export interface ProductionTemplate {
    readonly id: string;
    readonly name: string;
    readonly code: string;
    readonly default: boolean;
    readonly system: boolean;
    readonly version: number;
    readonly languages: readonly TemplateLang[];
    readonly paper: TemplatePaper;
    readonly signatureRequired: boolean;
}

export interface ProductionDeliberation {
    readonly id: string;
    readonly training: { readonly id: string; readonly code: string; readonly name: string };
    readonly session: Session;
    readonly closedAt: string | null;
    readonly run: number | null;
}

export interface ProductionSources {
    readonly years: readonly { readonly id: string; readonly label: string }[];
    readonly year: { readonly id: string; readonly label: string } | null;
    readonly kinds: readonly { readonly kind: TemplateKind; readonly source: TemplateSource; readonly perStudent: boolean }[];
    readonly trainings: readonly { readonly id: string; readonly code: string; readonly name: string }[];
    readonly deliberations: readonly ProductionDeliberation[];
    readonly templates: Readonly<Partial<Record<TemplateKind, readonly ProductionTemplate[]>>>;
    readonly signers: readonly { readonly id: string; readonly title: string; readonly name: string }[];
    readonly communication: boolean;
    readonly publishDefault: boolean;
    readonly queueFrom: number;
}

export interface ProductionPlan {
    readonly label: string;
    readonly source: TemplateSource;
    readonly training: string;
    readonly subjects: readonly { readonly id: string; readonly label: string; readonly warnings: readonly SubjectWarning[] }[];
    readonly ready: number;
    readonly warnings: number;
}

export interface JobSummary {
    readonly id: string;
    readonly kind: TemplateKind;
    readonly label: string;
    readonly template: { readonly id: string; readonly name: string } | null;
    readonly state: JobState;
    readonly total: number;
    readonly done: number;
    readonly failed: number;
    readonly createdAt: string;
    readonly createdBy: string;
    readonly startedAt: string | null;
    readonly finishedAt: string | null;
    readonly signatureRequired: boolean;
    readonly signer: { readonly title: string; readonly name: string } | null;
    readonly signedAt: string | null;
    readonly signedBy: string | null;
    readonly returnReason: string | null;
    readonly batchId: string;
}

export interface JobDetail extends JobSummary {
    readonly failures: readonly { readonly subject: string; readonly label: string; readonly reason: string }[];
    readonly options: Readonly<Record<string, unknown>>;
    readonly documents: number;
    readonly unsigned: number;
    readonly can: { readonly stop: boolean; readonly retry: boolean; readonly archive: boolean };
}

export interface SigningQueueItem extends JobSummary {
    readonly unsigned: number;
    readonly signerTitle: string | null;
    readonly delegated: boolean;
}

export interface SigningPage {
    readonly queue: readonly SigningQueueItem[];
    readonly signedThisMonth: number;
    readonly delegatedThisMonth: number;
    readonly returnedThisMonth: number;
    readonly me: {
        readonly id: string;
        readonly title: string;
        readonly signature: boolean;
        readonly stamp: boolean;
        readonly version: string;
        readonly delegate: { readonly id: string; readonly name: string } | null;
        readonly delegateFrom: string | null;
        readonly delegateTo: string | null;
    } | null;
    readonly candidates: readonly { readonly id: string; readonly name: string }[];
    readonly email: string;
}

export function getProductionSources(year: string | null): Promise<ProductionSources> {
    return page<ProductionSources>(`/api/documents/production/sources${year === null ? '' : `?year=${encodeURIComponent(year)}`}`);
}

export function getProductionPlan(kind: TemplateKind, template: string | null, target: Readonly<Record<string, string>>): Promise<ProductionPlan> {
    const params = new URLSearchParams({ kind, ...(template === null ? {} : { template }), ...target });

    return page<ProductionPlan>(`/api/documents/production/plan?${params.toString()}`);
}

/** L'aperçu d'un sujet avant production, barré « APERÇU ». */
export async function getProductionPreview(kind: TemplateKind, template: string | null, subject: string, deliberation: string | null, lang: string, training: string | null): Promise<string> {
    const params = new URLSearchParams({ kind, subject, lang, ...(template === null ? {} : { template }), ...(deliberation === null ? {} : { deliberation }), ...(training === null ? {} : { training }) });
    const response = await apiFetch(`/api/documents/production/preview?${params.toString()}`, { headers: { Accept: 'text/html' } });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return response.text();
}

export function getJobs(): Promise<{ readonly jobs: readonly JobSummary[] }> {
    return page<{ readonly jobs: readonly JobSummary[] }>('/api/documents/jobs');
}

export function getJob(id: string): Promise<JobDetail> {
    return page<JobDetail>(`/api/documents/jobs/${encodeURIComponent(id)}`);
}

export function jobArchivePath(id: string): string {
    return `/api/documents/jobs/${encodeURIComponent(id)}/archive`;
}

export function getSigning(): Promise<SigningPage> {
    return page<SigningPage>('/api/documents/signing');
}

export function requestSignatureCode(jobs: readonly string[]): Promise<{ readonly expiresAt: string; readonly email: string; readonly count: number }> {
    return apiJson('/api/documents/signing/request', { method: 'POST', body: JSON.stringify({ jobs }) });
}

export function confirmSignature(jobs: readonly string[], code: string): Promise<{ readonly count: number }> {
    return apiJson('/api/documents/signing/confirm', { method: 'POST', body: JSON.stringify({ jobs, code }) });
}

export async function uploadSignatureImage(kind: 'signature' | 'stamp', file: File): Promise<void> {
    const response = await apiFetch(`/api/documents/signing/me/${kind}`, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }
}

/** L'image de ma signature ou de mon cachet, en URL locale. */
export async function signatureImageUrl(kind: 'signature' | 'stamp'): Promise<string | null> {
    const response = await apiFetch(`/api/documents/signing/me/${kind}`, { headers: { Accept: 'image/*' } });

    return response.ok ? URL.createObjectURL(await response.blob()) : null;
}

/* D81 : les documents partagés. */

export type SharedPopulation = 'student' | 'teacher' | 'staff';

export interface SharedAudienceInput {
    readonly populations: readonly SharedPopulation[];
    readonly trainings: readonly string[];
    readonly classes: readonly string[];
}

export interface SharedFile {
    readonly name: string;
    readonly mime: string;
    readonly size: number;
}

export interface SharedSummary {
    readonly id: string;
    readonly title: string;
    readonly audience: {
        readonly populations: readonly SharedPopulation[];
        readonly trainings: readonly { readonly id: string; readonly label: string }[];
        readonly classes: readonly { readonly id: string; readonly label: string }[];
    };
    readonly from: string | null;
    readonly to: string | null;
    readonly acknowledgement: boolean;
    readonly version: number;
    readonly file: (SharedFile & { readonly at: string }) | null;
    readonly createdAt: string;
    readonly createdBy: string;
    readonly archivedAt: string | null;
    readonly available: boolean;
    readonly stats: { readonly audience: number; readonly opened: number; readonly accepted: number; readonly pending: number };
}

export interface SharedList {
    readonly documents: readonly SharedSummary[];
    readonly trainings: readonly { readonly id: string; readonly code: string; readonly name: string; readonly count: number }[];
    readonly classes: readonly { readonly id: string; readonly name: string; readonly training: string; readonly count: number }[];
    readonly year: string | null;
    readonly communication: boolean;
    readonly maxBytes: number;
}

export interface SharedItem extends SharedSummary {
    readonly versions: readonly (SharedFile & { readonly number: number; readonly createdAt: string; readonly createdBy: string })[];
    readonly readers: readonly { readonly id: string; readonly name: string; readonly population: SharedPopulation; readonly place: string | null; readonly openedAt: string | null; readonly acceptedAt: string | null }[];
    readonly truncated: boolean;
    readonly communication: boolean;
}

export interface SharedMine {
    readonly id: string;
    readonly title: string;
    readonly version: number;
    readonly sharedAt: string | null;
    readonly acknowledgement: boolean;
    readonly openedAt: string | null;
    readonly acceptedAt: string | null;
    readonly file: SharedFile | null;
    readonly until: string | null;
}

export interface SharedFields {
    readonly title: string;
    readonly audience: SharedAudienceInput;
    readonly from: string;
    readonly to: string;
    readonly acknowledgement: boolean;
    readonly notify?: boolean;
}

export function getSharedDocuments(archived: boolean): Promise<SharedList> {
    return page<SharedList>(`/api/documents/shared${archived ? '?archived=1' : ''}`);
}

export function getSharedDocument(id: string): Promise<SharedItem> {
    return page<SharedItem>(`/api/documents/shared/${encodeURIComponent(id)}`);
}

async function upload<T>(path: string, file: File): Promise<T> {
    const response = await apiFetch(path, { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as T;
}

export function createSharedDocument(file: File, fields: SharedFields): Promise<{ readonly id: string; readonly version: number }> {
    return upload(`/api/documents/shared?${new URLSearchParams({ name: file.name, values: JSON.stringify(fields) }).toString()}`, file);
}

export function replaceSharedDocument(id: string, file: File, notify: boolean): Promise<{ readonly id: string; readonly version: number }> {
    return upload(`/api/documents/shared/${encodeURIComponent(id)}/versions?${new URLSearchParams({ name: file.name, notify: notify ? '1' : '0' }).toString()}`, file);
}

export function sharedFilePath(id: string, version?: number): string {
    return `/api/documents/shared/${encodeURIComponent(id)}/file${version === undefined ? '' : `?version=${version}`}`;
}

export async function getMySharedDocuments(): Promise<readonly SharedMine[]> {
    return (await apiJson<{ readonly documents: readonly SharedMine[] }>('/api/documents/shared/mine')).documents;
}

export function acceptSharedDocument(id: string, version: number): Promise<{ readonly acceptedAt: string | null }> {
    return apiJson(`/api/documents/shared/${encodeURIComponent(id)}/accept`, { method: 'POST', body: JSON.stringify({ version }) });
}
